import { useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  apiClient,
  handleApiError,
  handleApiSuccess,
} from "@finitless/shared/lib";
import { useApiStore } from "@finitless/shared/stores";
import { useToast } from "@finitless/shared/hooks";

const urlIntegration =
  import.meta.env.VITE_STRIPE_CONNECT_URL ||
  "https://stripe-connect.plugins.orderingplus.com/";
const stripeConnectPluginKey = "stripe_connect_finitless";
const stripeConnectGatewayKey = "stripe_connect_link_finitless";

/**
 * Hook to manage Stripe Connect integration
 */
export const useStripeConnect = () => {
  const { t } = useTranslation("integrations");
  const toast = useToast();
  const queryClient = useQueryClient();

  /**
   * Query to get Stripe Connect status
   */
  const useStatus = ({ enabled = true } = {}) => {
    const projectCode = useApiStore((state) => state.project);

    const query = useQuery({
      queryKey: ["stripeConnectStatus", projectCode],
      queryFn: async () => {
        if (!projectCode) {
          return null;
        }
        const response = await apiClient.get(
          `/${projectCode}/stripe/connect/status`,
          {
            url: urlIntegration,
          },
        );
        return response?.result || response;
      },
      enabled: !!projectCode && !!enabled,
      staleTime: 5 * 60 * 1000, // 5 minutes
      refetchOnWindowFocus: false,
      refetchOnMount: true,
      refetchOnReconnect: false,
      retry: false,
    });

    useEffect(() => {
      if (!query.error) {
        return;
      }
      handleApiError(query.error, toast, {
        title: t("stripe.toasts.statusErrorTitle"),
      });
    }, [query.error, t, toast]);

    return query;
  };

  /**
   * Mutation to create Stripe onboarding link
   */
  const useOnboardingLink = () => {
    const projectCode = useApiStore((state) => state.project);

    return useMutation({
      mutationFn: async ({ refresh_url, return_url }) => {
        const response = await apiClient.post(
          `/${projectCode}/stripe/connect/onboarding-link`,
          {
            refresh_url,
            return_url,
          },
          {
            url: urlIntegration,
          },
        );
        return response?.result || response;
      },
      onError: (error) => {
        handleApiError(error, toast, {
          title: t("stripe.toasts.onboardingErrorTitle"),
        });
      },
    });
  };

  /**
   * Query to fetch installed plugins filtered by stripe connect plugin key
   */
  const useStripeConnectPlugins = () => {
    const query = useQuery({
      queryKey: ["plugins", stripeConnectPluginKey],
      queryFn: async () => {
        try {
          const response = await apiClient.get(
            `/plugins/key/${stripeConnectPluginKey}`,
          );
          const result = response?.result;
          return result ? [result] : [];
        } catch (error) {
          if (error?.status === 404) {
            return [];
          }
          throw error;
        }
      },
      staleTime: 5 * 60 * 1000, // 5 minutes
      refetchOnWindowFocus: false,
      refetchOnMount: true,
      refetchOnReconnect: false,
      retry: false,
    });

    useEffect(() => {
      if (!query.error) {
        return;
      }
      handleApiError(query.error, toast, {
        title: t("stripe.toasts.pluginsErrorTitle"),
      });
    }, [query.error, t, toast]);

    return query;
  };

  /**
   * Query to fetch the payment method associated with the plugin
   */
  const usePaymethod = ({ enabled = true } = {}) => {
    const query = useQuery({
      queryKey: ["paymethods", "gateway", stripeConnectGatewayKey],
      queryFn: async () => {
        try {
          const response = await apiClient.get(
            `/paymethods/gateway/${stripeConnectGatewayKey}`,
          );
          return response?.result || null;
        } catch (error) {
          if (error?.status === 404) {
            return null;
          }
          throw error;
        }
      },
      enabled: !!enabled,
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
      refetchOnMount: true,
      refetchOnReconnect: false,
      retry: false,
    });

    useEffect(() => {
      if (!query.error) {
        return;
      }
      handleApiError(query.error, toast, {
        title: t("stripe.toasts.paymethodErrorTitle"),
      });
    }, [query.error, t, toast]);

    return query;
  };

  /**
   * Mutation to enable/disable the payment method
   */
  const useTogglePaymethodEnabled = () => {
    return useMutation({
      mutationFn: async ({ paymethodId, enabled }) => {
        if (!paymethodId) {
          throw new Error("paymethodId is required");
        }

        const response = await apiClient.put(`/paymethods/${paymethodId}`, {
          enabled: !!enabled,
        });
        return response?.result || response;
      },
      onSuccess: (_data, variables) => {
        queryClient.invalidateQueries({ queryKey: ["paymethods"] });
        queryClient.removeQueries({ queryKey: ["paymentMethods"] });
        handleApiSuccess(
          variables?.enabled
            ? t("stripe.toasts.paymethodEnabled")
            : t("stripe.toasts.paymethodDisabled"),
          toast,
          { title: t("stripe.toasts.successTitle") },
        );
      },
      onError: (error) => {
        handleApiError(error, toast, {
          title: t("stripe.toasts.paymethodUpdateErrorTitle"),
        });
      },
    });
  };

  /**
   * Mutation to install Stripe Connect plugin
   */
  const useInstallStripeConnectPlugin = () => {
    return useMutation({
      mutationFn: async ({ url } = {}) => {
        const normalizedUrl = String(url ?? urlIntegration).trim();
        if (!normalizedUrl) {
          throw new Error("Plugin url is required");
        }

        const response = await apiClient.post("/plugins", {
          url: normalizedUrl,
        });
        return response?.result || response;
      },
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["plugins"] });
        queryClient.removeQueries({ queryKey: ["paymentMethods"] });
        handleApiSuccess(t("stripe.toasts.pluginInstalled"), toast, {
          title: t("stripe.toasts.successTitle"),
        });
      },
      onError: (error) => {
        handleApiError(error, toast, {
          title: t("stripe.toasts.pluginInstallErrorTitle"),
          defaultMessage: t("stripe.toasts.pluginInstallErrorMessage"),
        });
      },
    });
  };

  /**
   * Mutation to enable/disable an installed plugin by id
   */
  const useTogglePluginEnabled = () => {
    return useMutation({
      mutationFn: async ({ pluginId, enabled }) => {
        if (!pluginId) {
          throw new Error("pluginId is required");
        }

        const response = await apiClient.post(`/plugins/${pluginId}`, {
          enabled: !!enabled,
        });
        return response?.result || response;
      },
      onSuccess: (_data, variables) => {
        queryClient.invalidateQueries({ queryKey: ["plugins"] });
        handleApiSuccess(
          variables?.enabled
            ? t("stripe.toasts.pluginEnabled")
            : t("stripe.toasts.pluginDisabled"),
          toast,
          { title: t("stripe.toasts.successTitle") },
        );
      },
      onError: (error) => {
        handleApiError(error, toast, {
          title: t("stripe.toasts.pluginUpdateErrorTitle"),
          defaultMessage: t("stripe.toasts.pluginUpdateErrorMessage"),
        });
      },
    });
  };

  /**
   * Mutation to toggle sandbox mode
   */
  const useSandboxMode = () => {
    const projectCode = useApiStore((state) => state.project);

    return useMutation({
      mutationFn: async ({ sandbox }) => {
        const response = await apiClient.post(
          `/${projectCode}/stripe/connect/mode`,
          { sandbox: !!sandbox },
          { url: urlIntegration },
        );
        return response?.result || response;
      },
      onSuccess: (_data, variables) => {
        queryClient.invalidateQueries({ queryKey: ["stripeConnectStatus"] });
        handleApiSuccess(
          variables?.sandbox
            ? t("stripe.toasts.sandboxEnabled")
            : t("stripe.toasts.sandboxDisabled"),
          toast,
          { title: t("stripe.toasts.successTitle") },
        );
      },
      onError: (error) => {
        handleApiError(error, toast, {
          title: t("stripe.toasts.sandboxErrorTitle"),
        });
      },
    });
  };

  return {
    useStatus,
    useOnboardingLink,
    useStripeConnectPlugins,
    useInstallStripeConnectPlugin,
    useTogglePluginEnabled,
    usePaymethod,
    useTogglePaymethodEnabled,
    useSandboxMode,
  };
};

export default useStripeConnect;
